// Спільні елементи інтерфейсу. Стилі — у styles/theme.css (класи ui-*).
import React, { useEffect } from 'react';
import Icon from './Icon';

export { default as Icon, LogoMark } from './Icon';

// ── Форматування ────────────────────────────────────────────

const nf = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 });

// 12345.6 → «12 346»; null → «—»
export const fmtNum = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? '—' : nf.format(Number(v)));
export const fmtMoney = (v) => (v == null || v === '' ? '—' : `${fmtNum(v)} ₴`);
// 3.4 → «+3,4%», −1.7 → «−1,7%»
export const fmtPct = (v, digits = 1) => {
    if (v == null) return '—';
    const sign = v > 0 ? '+' : v < 0 ? '−' : '';
    return `${sign}${Math.abs(v).toFixed(digits).replace('.', ',')}%`;
};

// Мітки часу приходять з бази без поясу, а пояс MySQL і браузера можуть
// різнитись на кілька годин. Тому «зараз» беремо теж з бази: зведення
// повертають поле now, і різниця з годинником браузера запам'ятовується тут.
let serverOffset = 0;
const parseServerTime = (value) => new Date(String(value).replace(' ', 'T')).getTime();

export const syncServerClock = (serverNow) => {
    const t = serverNow ? parseServerTime(serverNow) : NaN;
    if (!Number.isNaN(t)) serverOffset = t - Date.now();
};

// «2026-10-13 14:02:11» (час бази, без поясу) → «12 хв тому»
export const timeAgo = (value) => {
    if (!value) return null;
    const t = parseServerTime(value);
    if (Number.isNaN(t)) return null;
    const min = Math.round((Date.now() + serverOffset - t) / 60000);
    if (min < 1) return 'щойно';
    if (min < 60) return `${min} хв тому`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} год тому`;
    const d = Math.round(h / 24);
    return `${d} дн тому`;
};

// «2026-09-29 …» → «29 вересня»
export const fmtDay = (value) => {
    if (!value) return null;
    const t = new Date(String(value).slice(0, 10) + 'T00:00:00');
    if (Number.isNaN(t.getTime())) return null;
    return t.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long' });
};

// Сезон у базі — код (offers_db.normalize_season), людям показуємо назву.
const SEASON_LABELS = { winter: 'Зима', summer: 'Літо', all_season: 'Всесезон' };
export const seasonLabel = (code) => (code ? SEASON_LABELS[code] || code : null);

// ── Кольори джерел ──────────────────────────────────────────
// Сталий колір за ключем: джерело однакове на дашборді, в таблицях і графіках.
const SOURCE_COLORS = {
    infoshina: '#60A5FA',
    ukrshina: '#FB923C',
    tshina: '#2DD4BF',
    rezina_ua: '#F472B6',
};
const EXTRA_COLORS = ['#FACC15', '#A3E635', '#C084FC', '#F87171'];

export const sourceColor = (key, index = 0) =>
    SOURCE_COLORS[key] || EXTRA_COLORS[index % EXTRA_COLORS.length];

export const SourceAvatar = ({ sourceKey, title, size = 36, index }) => (
    <span className="ui-avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: sourceColor(sourceKey, index) }}>
        {(title || sourceKey || '?').trim().charAt(0).toUpperCase()}
    </span>
);

export const SourceDot = ({ sourceKey, index }) => (
    <span className="ui-dot" style={{ background: sourceColor(sourceKey, index) }} />
);

// Дві літери бренду для «марки» в рядку списку: «Michelin» → «MI».
export const brandMark = (brand) => (brand || '?').replace(/[^A-Za-zА-Яа-яІіЇїЄєҐґ0-9]/g, '').slice(0, 2).toUpperCase();

// ── Компоненти ──────────────────────────────────────────────

export const PageHeader = ({ eyebrow, title, description, actions, back }) => (
    <header className="ui-page-head">
        <div style={{ minWidth: 0 }}>
            {back}
            {eyebrow && <div className="ui-eyebrow">{eyebrow}</div>}
            <h1>{title}</h1>
            {description && <p className="ui-page-desc">{description}</p>}
        </div>
        {actions && <div className="ui-page-actions">{actions}</div>}
    </header>
);

export const SearchField = ({ value, onChange, placeholder, large, style, autoFocus }) => (
    <label className={`ui-search${large ? ' ui-search--lg' : ''}`} style={style}>
        <Icon name="search" size={large ? 20 : 18} />
        <input
            type="search"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            autoFocus={autoFocus}
        />
    </label>
);

export const Switch = ({ checked, onChange, children }) => (
    <label className="ui-switch">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span>{children}</span>
    </label>
);

export const Alert = ({ kind = 'info', children, onClose }) => (
    <div className={`ui-alert ui-alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
        <Icon name={kind === 'ok' ? 'check' : 'alert'} />
        <div>{children}</div>
        {onClose && (
            <button className="ui-icon-btn ui-icon-btn--plain ui-icon-btn--sm" onClick={onClose} aria-label="Закрити повідомлення">
                <Icon name="close" size={18} />
            </button>
        )}
    </div>
);

export const Tile = ({ label, value, note, chip, chipKind, active, onClick }) => {
    const body = (
        <>
            <span className="ui-tile-label">
                <span>{label}</span>
                {chip != null && <span className={`ui-chip${chipKind ? ` ui-chip--${chipKind}` : ''}`}>{chip}</span>}
            </span>
            <span className="ui-tile-value">{value}</span>
            {note && <span className="ui-tile-note">{note}</span>}
        </>
    );
    if (!onClick) return <div className="ui-tile">{body}</div>;
    return (
        <button type="button" className={`ui-tile${active ? ' is-on' : ''}`} onClick={onClick} aria-pressed={!!active}>
            {body}
        </button>
    );
};

export const Modal = ({ title, onClose, children, width = 880, head }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        // Сторінка під вікном не прокручується разом з ним.
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    return (
        <div className="ui-modal-overlay" onClick={onClose}>
            <div className="ui-modal" style={{ maxWidth: width }} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}
                 onClick={e => e.stopPropagation()}>
                <div className="ui-modal-head">
                    <div style={{ minWidth: 0 }}>
                        {head}
                        {title && <h2>{title}</h2>}
                    </div>
                    <button className="ui-icon-btn" onClick={onClose} aria-label="Закрити">
                        <Icon name="close" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// Пагінація: «1–50 з 2 384» і кнопки сторінок навколо поточної.
export const Pager = ({ page, pageSize, total, onChange }) => {
    const pages = Math.ceil((total || 0) / pageSize);
    if (pages <= 1) {
        return total > 0 ? (
            <div className="ui-pager"><span className="muted num" style={{ fontSize: 13 }}>{fmtNum(total)} з {fmtNum(total)}</span></div>
        ) : null;
    }
    const from = page * pageSize + 1;
    const to = Math.min(total, (page + 1) * pageSize);

    const items = [];
    const push = (p) => items.push(p);
    const near = new Set([0, pages - 1, page - 1, page, page + 1].filter(p => p >= 0 && p < pages));
    [...near].sort((a, b) => a - b).forEach((p, i, arr) => {
        if (i > 0 && p - arr[i - 1] > 1) push('gap' + p);
        push(p);
    });

    return (
        <div className="ui-pager">
            <span className="muted num" style={{ fontSize: 13 }}>{fmtNum(from)}–{fmtNum(to)} з {fmtNum(total)}</span>
            <div className="ui-pager-pages">
                <button className="ui-icon-btn" onClick={() => onChange(page - 1)} disabled={page === 0} aria-label="Попередня сторінка">
                    <Icon name="chevronLeft" size={18} stroke={2} />
                </button>
                {items.map(p => typeof p === 'string' ? (
                    <span key={p} className="muted" style={{ padding: '0 4px' }}>…</span>
                ) : (
                    <button
                        key={p}
                        className={`ui-page-pill${p === page ? ' is-on' : ''}${Math.abs(p - page) > 1 ? ' is-far' : ''}`}
                        onClick={() => onChange(p)}
                        aria-current={p === page ? 'page' : undefined}
                    >
                        {p + 1}
                    </button>
                ))}
                <button className="ui-icon-btn" onClick={() => onChange(page + 1)} disabled={page + 1 >= pages} aria-label="Наступна сторінка">
                    <Icon name="chevronRight" size={18} stroke={2} />
                </button>
            </div>
        </div>
    );
};

// Завантаження файла через стилізовану кнопку.
export const FileButton = ({ accept, onChange, disabled, primary, children, icon = 'upload' }) => (
    <label className={`ui-btn${primary ? ' ui-btn--primary' : ''}`}
           style={disabled ? { opacity: 0.5, cursor: 'wait' } : undefined}>
        <Icon name={icon} />
        {children}
        <input type="file" accept={accept} onChange={onChange} disabled={disabled} />
    </label>
);

// Збереження Blob як файла (вивантаження Excel з сервера).
export const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
